import { createStore } from 'vuex';
import { db } from '@/firebase'; // 'auth' import removed from this line
import { doc, getDoc, collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';

// This variable will hold the function to unsubscribe from the Firestore listener
let unsubscribeFromRecordings = null;

export default createStore({
  state: {
    user: null,
    idTokenResult: null,
    authIsReady: false, // This flag will now signify that the initial auth check is done
    role: null,
    clientId: null,
    clientDetails: null,
    upcomingRecordings: [],
  },
  getters: {
    isSuperAdmin: (state) => state.role === 'super-admin',
    isClientAdmin: (state) => state.role === 'admin',
    displayName: (state) => state.user?.displayName || state.user?.email || 'User',
    userEmail: (state) => state.user?.email || '',
    nextRecording(state) {
      if (!state.upcomingRecordings || state.upcomingRecordings.length === 0) {
        return null;
      }
      return state.upcomingRecordings[0];
    },
  },
  mutations: {
    setUser: (state, payload) => { state.user = payload; },
    setIdTokenResult: (state, payload) => { state.idTokenResult = payload; },
    setAuthIsReady: (state, payload) => { state.authIsReady = payload; },
    setRole: (state, payload) => { state.role = payload; },
    setClientId: (state, payload) => { state.clientId = payload; },
    setClientDetails: (state, payload) => { state.clientDetails = payload; },
    setUpcomingRecordings: (state, payload) => { state.upcomingRecordings = payload; },
  },
  actions: {
    // This new action takes the user object from onAuthStateChanged and populates the store.
    async handleAuthStateChange({ commit, dispatch }, user) {
      // If there's no user, reset the state to logged-out
      if (!user) {
        commit('setUser', null);
        commit('setIdTokenResult', null);
        commit('setRole', null);
        commit('setClientId', null);
        commit('setClientDetails', null);
        dispatch('unsubscribeFromUpcomingRecordings'); // Clean up listener
        commit('setAuthIsReady', true); // Auth check is complete
        return;
      }
      
      // If there is a user, fetch their data and claims
      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const claims = idTokenResult.claims;
        commit('setUser', user);
        commit('setIdTokenResult', idTokenResult);
        commit('setRole', claims.role || null);
        commit('setClientId', claims.clientId || null);

        // If the user is associated with a client, fetch client details
        if (claims.clientId) {
          const clientDocRef = doc(db, 'clients', claims.clientId);
          const clientDocSnap = await getDoc(clientDocRef);
          if (clientDocSnap.exists()) {
            commit('setClientDetails', clientDocSnap.data());
          }
          // Subscribe to this client's upcoming recordings
          dispatch('subscribeToUpcomingRecordings', claims.clientId);
        } else {
          // If user has no client (e.g., super-admin), ensure no lingering subscription
          dispatch('unsubscribeFromUpcomingRecordings');
        }
      } catch (error) {
        console.error("Error during auth state change handling:", error);
        // In case of error, reset state to be safe
        commit('setUser', null);
        commit('setIdTokenResult', null);
        commit('setRole', null);
        commit('setClientId', null);
        commit('setClientDetails', null);
        dispatch('unsubscribeFromUpcomingRecordings');
      } finally {
        // Signal that the initial authentication process is complete
        commit('setAuthIsReady', true);
      }
    },

    // This action sets up the listener for upcoming recordings
    subscribeToUpcomingRecordings({ commit }, clientId) {
      // --- START: DEFINITIVE FIX FOR ZOMBIE LISTENER ---
      if (unsubscribeFromRecordings) {
        unsubscribeFromRecordings();
        unsubscribeFromRecordings = null; // Set to null before creating a new one
      }
      // --- END: DEFINITIVE FIX FOR ZOMBIE LISTENER ---
      const recordingsQuery = query(
        collection(db, 'submissions'),
        where('clientId', '==', clientId),
        where('recordingDate', '>=', new Date()),
        orderBy('recordingDate', 'asc')
      );
      unsubscribeFromRecordings = onSnapshot(recordingsQuery, (snapshot) => {
        const recordings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        commit('setUpcomingRecordings', recordings);
      }, (error) => {
        console.error("Error in 'subscribeToUpcomingRecordings' snapshot listener:", error);
      });
    },

    // This new action explicitly cleans up the recordings listener and data
    unsubscribeFromUpcomingRecordings({ commit }) {
        if (unsubscribeFromRecordings) {
          unsubscribeFromRecordings();
          unsubscribeFromRecordings = null;
        }
        commit('setUpcomingRecordings', []);
    }
  },
  modules: {}
});